/**
 * Payments entity config (11F.7) — drives EntityTable on the Payments list.
 *
 * Reference: `Payments.png` (information architecture only; D13 governs the
 * look). Column keys double as the `sort` query-param value and must match
 * the backend's `list_payments_paged` sort map
 * (payment_date | amount | method | student_name | invoice_number).
 *
 * The **Refund** column is real, not a proxy for voiding: 11F.7 added a
 * distinct refund record (money returned) alongside the pre-existing void
 * (recorded in error). A row can show a refund while remaining Active.
 */

import { Badge } from '@/components/common/Badge';
import type { EntityTableConfig } from '@/components/entity/types';
import type { PaymentResponse } from '../api/billing';
import { formatMinorAmount, paymentMethodLabel, paymentStatusColor } from './ui';
import { DateCell, MutedCell, NumericCell } from './billingEntityComponents';

export const paymentsTableConfig: EntityTableConfig<PaymentResponse> = {
  entityName: 'payment',
  apiPath: '/v1/payments',
  routeBase: '/payments',
  testIdPrefix: 'candeza-payment',
  defaultSort: 'payment_date',
  defaultOrder: 'desc',
  defaultFilters: {},

  columns: [
    {
      key: 'payment_date',
      header: 'Date',
      isPrimary: true,
      sortable: true,
      helpKey: 'payments.date',
      render: (p) => <DateCell value={p.payment_date} />,
    },
    {
      key: 'student_name',
      header: 'Student',
      sortable: true,
      helpKey: 'payments.student',
      render: (p) => p.student_name || <MutedCell>Unknown student</MutedCell>,
    },
    {
      key: 'invoice_number',
      header: 'Invoice #',
      sortable: true,
      helpKey: 'payments.invoice_number',
      render: (p) => p.invoice_number || <MutedCell>—</MutedCell>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      helpKey: 'payments.amount',
      render: (p) => <NumericCell>{formatMinorAmount(p.amount)}</NumericCell>,
    },
    {
      key: 'refunded_amount',
      header: 'Refund',
      hideOnMobile: true,
      helpKey: 'payments.refunded',
      render: (p) =>
        (p.refunded_amount ?? 0) > 0 ? (
          <NumericCell color="#b45309">
            −{formatMinorAmount(p.refunded_amount ?? 0)}
          </NumericCell>
        ) : (
          <MutedCell>—</MutedCell>
        ),
    },
    {
      key: 'method',
      header: 'Mode',
      sortable: true,
      hideOnMobile: true,
      helpKey: 'payments.method',
      render: (p) => paymentMethodLabel(p.method),
    },
    {
      key: 'reference_number',
      header: 'Reference',
      hideOnMobile: true,
      helpKey: 'payments.reference',
      render: (p) => p.reference_number || <MutedCell>—</MutedCell>,
    },
    {
      key: 'status',
      header: 'Status',
      helpKey: 'payments.status',
      render: (p) => (
        <Badge variant="default" dot style={{ color: paymentStatusColor(p.status) }}>
          {p.status === 'voided' ? 'Voided' : 'Active'}
        </Badge>
      ),
    },
  ],

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      helpKey: 'payments.filter.status',
      options: [
        { value: 'all', label: 'All payments' },
        { value: 'active', label: 'Active' },
        { value: 'voided', label: 'Voided' },
      ],
    },
  ],

  // Payments are financial records — never archived or edited from a list
  // row. The row click opens the invoice the payment belongs to, which is
  // where every corrective action (void, refund) legitimately lives.
  rowActions: ['view'],
};
